import { useEffect, useState } from "react";
import { Link } from "wouter";
import AccountActions from "@/components/AccountActions";
import BrandLockup from "@/components/BrandLockup";
import DeleteRecordingControl from "@/components/DeleteRecordingControl";
import FlightPath from "@/components/FlightPath";
import { useAuth } from "@/contexts/AuthContext";
import { voicePages } from "@/content/voicePages";
import { buildVoiceLibrary, type CurrentRecordingSummary } from "@/lib/library";
import { friendlyErrorMessage } from "@/lib/friendlyError";
import { supabase } from "@/lib/supabase";

const recordingsTable = (client: NonNullable<typeof supabase>) =>
  (client as unknown as { from: (table: "recordings") => any }).from("recordings");

function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Date unavailable";
  return new Intl.DateTimeFormat(undefined, { year: "numeric", month: "short", day: "numeric" }).format(date);
}

function formatDuration(value: number): string {
  const seconds = Math.max(0, Math.floor(value));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

export default function Library() {
  const { session, isLoading, isConfigured } = useAuth();
  const [recordings, setRecordings] = useState<CurrentRecordingSummary[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!session || !supabase) return;
    const userId = session.user.id;
    let cancelled = false;
    setIsFetching(true);
    setError(null);

    async function loadRecordings() {
      try {
        const recordingResult = await recordingsTable(supabase!)
          .select("page_number,duration_seconds,created_at,is_current")
          .eq("user_id", userId)
          .eq("is_current", true)
          .order("created_at", { ascending: false });
        if (recordingResult.error) throw recordingResult.error;
        if (!cancelled) {
          setRecordings((recordingResult.data ?? []) as CurrentRecordingSummary[]);
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(friendlyErrorMessage(loadError, "account", "We couldn’t load your voice pages. Check your connection and try again."));
        }
      } finally {
        if (!cancelled) setIsFetching(false);
      }
    }

    void loadRecordings();
    return () => { cancelled = true; };
  }, [reloadKey, session]);

  const pages = buildVoiceLibrary(voicePages, recordings);
  const recordedCount = pages.filter((page) => page.recording).length;

  return (
    <div className="paper-shell inner-shell">
      <header className="site-header">
        <BrandLockup />
        {session && <AccountActions />}
      </header>

      <main className="library-layout">
        <div className="library-intro">
          <p className="eyebrow"><span className="eyebrow-rule" /> Your journal</p>
          <h1 id="library-title">Your voice<br />pages<span className="title-period">.</span></h1>
          <p className="library-description">
            {isLoading ? "Checking your sign-in…" : session
              ? `${recordedCount} of 11 pages recorded. Your stories stay private to your account.`
              : "Sign in to see and listen to your saved voice pages."}
          </p>
          {session && !isLoading && (
            <div className="library-progress" aria-label={`${recordedCount} of 11 voice pages recorded`}>
              <div className="library-progress-track" role="progressbar" aria-valuemin={0} aria-valuemax={11} aria-valuenow={recordedCount} aria-label="Voice pages recorded">
                <span style={{ width: `${(recordedCount / 11) * 100}%` }} />
              </div>
              <span className="library-progress-count">{recordedCount} / 11</span>
            </div>
          )}
        </div>

        {isLoading ? (
          <p className="auth-status" role="status">Checking your sign-in…</p>
        ) : !session ? (
          <section className="library-empty-state">
            <p className="library-empty-copy">Your saved recordings are available after you sign in.</p>
            <Link href={`/login?next=${encodeURIComponent("/library")}`} className="primary-action">
              Sign in to view your pages <span aria-hidden="true">→</span>
            </Link>
          </section>
        ) : !isConfigured ? (
          <p className="setup-message" role="status">Your voice pages aren’t available just yet. Please try again later.</p>
        ) : (
          <>
            {error && (
              <div className="error-message library-error" role="alert">
                <span>{error}</span>
                <button type="button" className="secondary-action" onClick={() => setReloadKey((key) => key + 1)}>
                  Try again
                </button>
              </div>
            )}
            {isFetching && <p className="auth-status" role="status">Loading your voice pages…</p>}
            {!isFetching && !error && (
              <>
              <FlightPath className="library-divider" />
              <ol className="library-grid" aria-labelledby="library-title">
                {pages.map((page) => {
                  const recording = page.recording;
                  return (
                    <li key={page.number} className="library-card">
                      <div className="library-card-heading">
                        <span className="library-chapter">{page.number} · {page.chapterTitle}</span>
                        <span className={recording ? "library-status is-recorded" : "library-status"}>
                          {recording ? "Recorded" : "Not yet"}
                        </span>
                      </div>
                      <h2 className="library-prompt">{page.prompt}</h2>
                      {recording ? (
                        <p className="library-meta">
                          {formatDuration(recording.duration_seconds)} · Recorded {formatDate(recording.created_at)}
                        </p>
                      ) : (
                        <p className="library-meta">Your story is waiting here.</p>
                      )}
                      <Link href={`/v/${page.number}`} className="secondary-action library-open">
                        {recording ? "Listen or record a new version" : "Record this page"}
                        <span aria-hidden="true">→</span>
                      </Link>
                      {recording && session && (
                        <DeleteRecordingControl
                          userId={session.user.id}
                          pageNumber={page.number}
                          onDeleted={() => setReloadKey((key) => key + 1)}
                        />
                      )}
                    </li>
                  );
                })}
              </ol>
              </>
            )}
          </>
        )}
        {session && <AccountActions placement="library" />}
      </main>

      <footer className="site-footer">
        <Link href="/" className="back-link">← Home, Twice</Link>
        <nav className="legal-footer-links" aria-label="Legal">
          <Link href="/privacy">Privacy Policy</Link>
          <Link href="/terms">Terms</Link>
        </nav>
        <span>Published by Kwaderno &amp; Co.</span>
      </footer>
    </div>
  );
}
